import { useEffect } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * *"We couldn't send your application"* — the Bullhorn write failed [KPW-1c; Figma
 * `Edge cases / E4 · Submission failed (Bullhorn / Workato) — Desktop`, 1440, read live 2026-10-08].
 *
 * **It speaks for the Bullhorn write only, never for Workato** [0074; OQ-77 open]. The frame's name pairs two
 * failures, and the kiosk can observe only the first. Its copy says nothing about the recipe, the welcome
 * email or onboarding. Keep it that way: the kiosk has no signal for any of them.
 *
 * **It routes to a person and nowhere else** [BR-K7; 0046]. `Get help` goes to the same hand-off the generic
 * no-submission screen gives (`FinishScreen`'s branch). Nothing here sends a second `POST /submissions`, and
 * there is no "try again": a retry is where a substitute status would hide [0074].
 *
 * **The promise is made here, so the queue entry is raised here.** The body says *"A representative will
 * finish this with you"*. That is the rule `FinishScreen` and `DrugTestScreen` follow: the screen that
 * promises a person raises the entry. `summonRepresentative` is once per candidate session, so the hand-off
 * after `Get help` adds no second row. (The one screen whose raise is not its own effect is `SeeARepScreen`:
 * its control is derived in `KioskScreen`, so it raises through `KioskScreen`'s `raisesOnArrival`.)
 *
 * ⭐ **The entry names the failed write** [0486]: the screen opening is the ask, so it names
 * `submissionFailedScreen` and the api raises *Application not sent* — not *ready for recruiter*, which tells the
 * recruiter the candidate finished the flow. Only this screen names it.
 *
 * Only the Desktop width is drawn. 375 and 820 follow `SeeARepScreen` (`04 · See a rep`), the hand-off this
 * frame copies at 1440: the same header, icon, centred column and footer.
 */
export function SubmissionFailedScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const back = useBack();
  const goTo = useKioskSession((state) => state.goTo);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  useEffect(() => {
    summonRepresentative(undefined, HANDOFF_CONTROL.submissionFailedScreen);
  }, [summonRepresentative]);

  return (
    <KioskScreen>
      <KioskCard
        footer={
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            <Button onClick={() => goTo(SCREEN.finish)} variant="secondary">
              {t('submissionFailed.getHelp')}
            </Button>
          </div>
        }
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('seeARep.badge')} tone="rep" />
          </>
        }
      >
        <div className="flex w-full flex-col items-center gap-3 pb-8 text-center">
          <span
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-[var(--radius-full)] bg-[var(--primary-muted)]"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5Z"
                stroke="var(--primary)"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <h1
            className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('submissionFailed.heading')}
          </h1>
          <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
            {t('submissionFailed.body')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
